import { useEffect, useState } from 'react';

import { loadStripe } from '@stripe/stripe-js';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import useSelectedPriceId from '~/hooks/useSelectedPriceId';
import type { Publication } from '~/interfaces/publication';
import type { PremiumOffer, Price, PricingDetails } from '~/interfaces/subscriptions';
import type { UpgradePage } from '~/interfaces/upgrade_page';

import useSignupRedirects from './useUtils/useSignupRedirects';

interface Props {
  publication: Publication;
  upgradePage: UpgradePage;
  pricingDetails: PricingDetails;
}

const initStripe = (publishableKey: string, stripeAccountId: string) => {
  return loadStripe(publishableKey, {
    stripeAccount: stripeAccountId,
  });
};

const findPrice = (pricingDetails: PricingDetails, priceId: string) => {
  return pricingDetails.tiers.flatMap((tier) => tier.prices).find((price) => price.id === priceId);
};

const findPremiumOffer = (pricingDetails: PricingDetails, priceId: string) => {
  if (priceId === 'free') {
    return null;
  }

  const price = findPrice(pricingDetails, priceId);
  if (!price) {
    return null;
  }

  return price.premium_offer;
};

const useSubscriberCheckout = ({ publication, upgradePage, pricingDetails }: Props) => {
  const { nextPage, isInProgress } = useSignupRedirects(publication);
  const [stripeSession, setStripeSession] = useState<any>(null);
  const {
    enable_external_stripe_checkout: isExternalCheckout,
    enable_billing_address_collection: isCollectingBillingAddress,
    most_popular_price_id: mostPopularPriceId,
  } = upgradePage?.main_section;

  const { priceId, setPriceId } = useSelectedPriceId(mostPopularPriceId, pricingDetails.tiers || [], upgradePage);
  const [price, setPrice] = useState<Price | undefined>(undefined);
  const [premiumOffer, setPremiumOffer] = useState<PremiumOffer | undefined | null>(null);

  const cardlessPremiumOffer = premiumOffer && !premiumOffer.card_required;

  const handleRedirectAfterPayment = () => {
    const selectedTier = pricingDetails.tiers.find((tier) => tier.prices.find((price) => price.id === priceId));

    if (selectedTier?.redirect_url) {
      window.location.replace(selectedTier?.redirect_url);
      return;
    }

    nextPage();
  };

  useEffect(() => {
    if (!stripeSession && window) {
      const appGlobals = getAppGlobals();

      setStripeSession(() => initStripe(appGlobals.STRIPE_PUBLISHABLE_KEY, publication.stripe_user_id ?? ''));
    }
  }, [stripeSession]);

  useEffect(() => {
    setPrice(() => findPrice(pricingDetails, priceId));
    setPremiumOffer(() => findPremiumOffer(pricingDetails, priceId));
  }, [pricingDetails, priceId, setPremiumOffer]);

  return {
    nextPage,
    isInProgress,
    handleRedirectAfterPayment,
    stripeSession,
    setStripeSession,
    isExternalCheckout,
    isCollectingBillingAddress,
    mostPopularPriceId,
    priceId,
    setPriceId,
    price,
    setPrice,
    premiumOffer,
    setPremiumOffer,
    cardlessPremiumOffer,
  };
};

export default useSubscriberCheckout;
